import React, { useMemo, useState, useCallback } from 'react';
import { trimEnd } from 'lodash';
import { Nav, NavItem, NavLink, Card, CardBody } from 'reactstrap';
import {
  PanelHeader,
  Icon,
  ReplaceLink,
  Loading as LoadingIndicator,
} from 'js/components';
import ConfirmModal from 'js/components/modal/ConfirmModal';
import { Link, useParams, useRouteMatch } from 'react-router-dom';
import NoManagedProductsCard from './NoManagedProductsCard';
import ManagedProduct from './ManagedProduct';

// import styles from './ManagedProductsDashboard.module.scss';

function ManagedProductsDashboard({
  hasLoaded,
  isLoading,
  managedProducts,
  onAddManagedProduct,
  onUpdateManagedProduct,
}) {
  const { workspaceId } = useParams();
  const searchParams = new URLSearchParams(window.location.search);
  const match = useRouteMatch();
  const showArchived = searchParams.has('archived');

  const [isAddingManagedProduct, setIsAddingManagedProduct] = useState(false);
  const [managedProductToArchive, setManagedProductToArchive] = useState(null);

  const filteredManagedProducts = useMemo(() => {
    return (
      managedProducts?.filter((managedProduct) => {
        return showArchived
          ? managedProduct.archived_at !== null
          : managedProduct.archived_at === null;
      }) ?? []
    );
  }, [managedProducts, showArchived]);

  const handleArchiveManagedProduct = useCallback(async (managedProduct) => {
    setManagedProductToArchive(managedProduct);
  }, []);

  const handleRestoreManagedProduct = useCallback(
    async (managedProduct) => {
      return await onUpdateManagedProduct({
        id: managedProduct.id,
        archived_state: null,
      });
    },
    [onUpdateManagedProduct]
  );

  const toggleAddManagedProduct = useCallback(() => {
    setIsAddingManagedProduct(!isAddingManagedProduct);
  }, [isAddingManagedProduct]);

  const handleArchiveModalConfirm = useCallback(async () => {
    await onUpdateManagedProduct({
      id: managedProductToArchive.id,
      archived_state: 'archived',
    });
    setManagedProductToArchive(null);
  }, [onUpdateManagedProduct, managedProductToArchive]);

  return (
    <div>
      <PanelHeader size="flex">
        <div className="panel-body-no-label px-4">
          <div className="content-fluid">
            <div className="row">
              <div className="col-12 col-md-9">
                <h1>Products</h1>
              </div>
              <div className="panel-actions col-12 col-md-3">
                <Link
                  to={`/w/${workspaceId}/pm/products/new`}
                  className="create-link btn btn-primary"
                >
                  <Icon icon="plus" className="btn-icon-left" /> Create
                </Link>
              </div>
            </div>
          </div>
        </div>
        <div className="container-fluid">
          <Nav className="tab-nav nav-underline">
            <NavItem>
              <NavLink
                tag={ReplaceLink}
                active={!searchParams.has('archived')}
                data-view="active"
                to={`${trimEnd(match.url, '/')}`}
              >
                Active
              </NavLink>
            </NavItem>
            <NavItem>
              <NavLink
                tag={ReplaceLink}
                active={searchParams.has('archived')}
                data-view="archived"
                to={`${trimEnd(match.url, '/')}?archived`}
              >
                Archived
              </NavLink>
            </NavItem>
          </Nav>
        </div>
      </PanelHeader>
      <div className="content" style={{ position: 'relative' }}>
        {!hasLoaded && !filteredManagedProducts?.length && (
          <Card>
            <CardBody>
              <p></p>
            </CardBody>
          </Card>
        )}

        {hasLoaded &&
          !filteredManagedProducts?.length &&
          !isAddingManagedProduct && (
            <NoManagedProductsCard
              type={showArchived ? 'archived' : 'default'}
              onAddManagedProduct={toggleAddManagedProduct}
            />
          )}

        {hasLoaded &&
          filteredManagedProducts?.length &&
          filteredManagedProducts?.length > 0 &&
          filteredManagedProducts.map((managedProduct) => (
            <ManagedProduct
              key={managedProduct.id}
              managedProduct={managedProduct}
              onArchive={handleArchiveManagedProduct}
              onRestore={handleRestoreManagedProduct}
            />
          ))}

        {isLoading && (
          <LoadingIndicator
            style={{ position: 'absolute', right: '25px', top: '10px' }}
          />
        )}
      </div>
      <ConfirmModal
        isOpen={!!managedProductToArchive}
        title="Archive managed product?"
        onConfirm={handleArchiveModalConfirm}
        onCancel={() => setManagedProductToArchive(null)}
        confirmButtonLabel="Archive"
      >
        <p>
          Please confirm that you want to archive{' '}
          <strong>
            {managedProductToArchive?.name || 'this managed product'}
          </strong>
          .
        </p>
      </ConfirmModal>
    </div>
  );
}

export default ManagedProductsDashboard;
